import React, { useState } from 'react';
import { Sparkles, Loader2, Plus, X, Info, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'react-hot-toast';

/**
 * CampusSection - Section 3: Campus & Culture
 *
 * Fields:
 * - Spaces (dynamic array, max 5): name + behavior_it_invites
 * - Clubs (dynamic array, max 3): name + community_signal
 * - Location Advantage (optional textarea, 400 chars)
 */
export default function CampusSection({ data, onChange, collegeData }) {
  const [aiLoading, setAiLoading] = useState(false);
  const [showGuidance, setShowGuidance] = useState(false);

  // Get AI suggestions for this section
  const getAISuggestions = async () => {
    // Validate we have college data
    if (!collegeData || !collegeData.id) {
      toast.error('College data not loaded yet. Please wait a moment and try again.');
      console.error('Missing college data:', collegeData);
      return;
    }

    setAiLoading(true);
    try {
      console.log('🎓 Requesting AI suggestions for campus section:', {
        college_id: collegeData.id,
        has_global_data: !!collegeData.global
      });

      const response = await fetch('/api/why-school/enhance-section', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          college_id: collegeData.id,
          section: 'campus',
          existing_data: data,
          global_college_data: collegeData.global
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('API error:', response.status, errorData);
        throw new Error(errorData.error || 'Failed to get suggestions');
      }

      const suggestions = await response.json();
      console.log('✅ Received suggestions:', suggestions);

      // Merge AI suggestions into existing data (fill in "behavior" and "signal" fields)
      const updates = { ...data };
      let hasUpdates = false;

      // Merge space "behavior" suggestions
      if (suggestions.space_suggestions && suggestions.space_suggestions.length > 0 && data.spaces) {
        updates.spaces = data.spaces.map(space => {
          const suggestion = suggestions.space_suggestions.find(s =>
            s.name && space.name && s.name.toLowerCase().includes(space.name.toLowerCase().substring(0, 10))
          );
          if (suggestion && suggestion.behavior_suggestion && !space.behavior_it_invites) {
            hasUpdates = true;
            return { ...space, behavior_it_invites: suggestion.behavior_suggestion };
          }
          return space;
        });
      }

      // Merge club "community signal" suggestions
      if (suggestions.club_suggestions && suggestions.club_suggestions.length > 0 && data.clubs) {
        updates.clubs = data.clubs.map(club => {
          const suggestion = suggestions.club_suggestions.find(s =>
            s.name && club.name && s.name.toLowerCase().includes(club.name.toLowerCase().substring(0, 10))
          );
          if (suggestion && suggestion.community_signal_suggestion && !club.community_signal) {
            hasUpdates = true;
            return { ...club, community_signal: suggestion.community_signal_suggestion };
          }
          return club;
        });
      }

      if (hasUpdates) {
        onChange(updates);
        toast.success('AI suggestions added to your existing content! Review and edit as needed.');
      } else {
        toast.success('Your content already has explanations. AI won\'t overwrite them!');
      }
    } catch (err) {
      console.error('Error getting AI suggestions:', err);
      toast.error(`Could not load AI suggestions: ${err.message}`);
    } finally {
      setAiLoading(false);
    }
  };

  // === SPACES ===
  const addSpace = () => {
    const spaces = data.spaces || [];
    if (spaces.length >= 5) {
      toast.error('Maximum 5 spaces allowed');
      return;
    }
    onChange({
      ...data,
      spaces: [...spaces, { name: '', description: '', behavior_it_invites: '' }]
    });
  };

  const updateSpace = (index, field, value) => {
    const spaces = [...(data.spaces || [])];
    spaces[index] = { ...spaces[index], [field]: value };
    onChange({ ...data, spaces });
  };

  const removeSpace = (index) => {
    const spaces = [...(data.spaces || [])];
    spaces.splice(index, 1);
    onChange({ ...data, spaces });
  };

  // === CLUBS ===
  const addClub = () => {
    const clubs = data.clubs || [];
    if (clubs.length >= 3) {
      toast.error('Maximum 3 clubs allowed');
      return;
    }
    onChange({
      ...data,
      clubs: [...clubs, { name: '', description: '', community_signal: '' }]
    });
  };

  const updateClub = (index, field, value) => {
    const clubs = [...(data.clubs || [])];
    clubs[index] = { ...clubs[index], [field]: value };
    onChange({ ...data, clubs });
  };

  const removeClub = (index) => {
    const clubs = [...(data.clubs || [])];
    clubs.splice(index, 1);
    onChange({ ...data, clubs });
  };

  return (
    <div className="space-y-8">
      {/* Section Guidance */}
      <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
        <button
          onClick={() => setShowGuidance(!showGuidance)}
          className="flex items-center justify-between w-full text-left"
        >
          <div className="flex items-center space-x-2">
            <Info className="w-5 h-5 text-indigo-600" />
            <span className="font-semibold text-indigo-900">Section Guidance</span>
          </div>
          {showGuidance ? (
            <ChevronUp className="w-5 h-5 text-indigo-600" />
          ) : (
            <ChevronDown className="w-5 h-5 text-indigo-600" />
          )}
        </button>

        {showGuidance && (
          <div className="mt-4 space-y-3 text-sm text-indigo-800">
            <p className="font-medium">What This Section Does:</p>
            <p>
              Show how the campus environment—its spaces, clubs, and location—enables specific
              behaviors or communities that align with who you are.
            </p>
            <div className="bg-white rounded p-3 border border-indigo-200">
              <p className="font-medium mb-2">Strong Example:</p>
              <p className="italic">
                Space: "The Stata Center's collision spaces"
                <br />
                Behavior: "I'd run into CS majors debugging hardware while I'm prototyping
                interfaces—our accidental conversations would lead to better designs."
              </p>
            </div>
            <div className="bg-white rounded p-3 border border-indigo-200">
              <p className="font-medium mb-2">Weak Example:</p>
              <p className="line-through italic">
                "The beautiful campus has many clubs and organizations where I can get involved."
              </p>
              <p className="text-red-700 text-xs mt-1">
                ❌ Too generic—no specific spaces, no personal connection to behavior
              </p>
            </div>
          </div>
        )}
      </div>

      {/* AI Suggestions Button */}
      <div className="flex justify-end">
        <button
          onClick={getAISuggestions}
          disabled={aiLoading}
          className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {aiLoading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>Loading Suggestions...</span>
            </>
          ) : (
            <>
              <Sparkles className="w-4 h-4" />
              <span>Get AI Suggestions</span>
            </>
          )}
        </button>
      </div>

      {/* Field 1: Spaces */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm font-semibold text-gray-900">
            Campus Spaces (up to 5)
          </label>
          <button
            onClick={addSpace}
            disabled={(data.spaces || []).length >= 5}
            className="flex items-center space-x-1 text-sm text-indigo-600 hover:text-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Plus className="w-4 h-4" />
            <span>Add Space</span>
          </button>
        </div>
        <p className="text-sm text-gray-600 mb-4">
          Name specific places on campus (library, makerspace, quad, dining hall) and describe
          what behavior that space invites or enables.
        </p>

        {/* Spaces List */}
        <div className="space-y-4">
          {(data.spaces || []).map((space, index) => (
            <div key={index} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <div className="flex items-start space-x-3">
                <div className="flex-1 space-y-3">
                  {/* Name */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Space Name #{index + 1}
                    </label>
                    <input
                      type="text"
                      value={space.name || ''}
                      onChange={(e) => updateSpace(index, 'name', e.target.value)}
                      placeholder="e.g., The Stata Center collision spaces"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    />
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      What is this space?
                    </label>
                    <textarea
                      value={space.description || ''}
                      onChange={(e) => updateSpace(index, 'description', e.target.value)}
                      placeholder="Brief description of what this space is and what happens there"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-y min-h-[60px]"
                      style={{ height: 'auto', minHeight: '60px' }}
                      onInput={(e) => {
                        e.target.style.height = 'auto';
                        e.target.style.height = e.target.scrollHeight + 'px';
                      }}
                    />
                  </div>

                  {/* Behavior it invites */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      What behavior does this space invite?
                    </label>
                    <textarea
                      value={space.behavior_it_invites || ''}
                      onChange={(e) => updateSpace(index, 'behavior_it_invites', e.target.value)}
                      placeholder="e.g., Accidental collisions between CS majors and designers lead to better prototypes"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-y min-h-[60px]"
                      style={{ height: 'auto', minHeight: '60px' }}
                      onInput={(e) => {
                        e.target.style.height = 'auto';
                        e.target.style.height = e.target.scrollHeight + 'px';
                      }}
                    />
                  </div>
                </div>

                {/* Remove Button */}
                <button
                  onClick={() => removeSpace(index)}
                  className="flex-shrink-0 p-2 text-gray-400 hover:text-red-600 transition"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}

          {/* Empty State */}
          {(!data.spaces || data.spaces.length === 0) && (
            <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
              <p className="text-sm text-gray-600 mb-3">No spaces added yet</p>
              <button
                onClick={addSpace}
                className="inline-flex items-center space-x-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition"
              >
                <Plus className="w-4 h-4" />
                <span>Add Your First Space</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Field 2: Clubs */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm font-semibold text-gray-900">
            Clubs & Organizations (up to 3)
          </label>
          <button
            onClick={addClub}
            disabled={(data.clubs || []).length >= 3}
            className="flex items-center space-x-1 text-sm text-indigo-600 hover:text-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Plus className="w-4 h-4" />
            <span>Add Club</span>
          </button>
        </div>
        <p className="text-sm text-gray-600 mb-4">
          Name specific clubs or student organizations and explain what community or value
          they signal. Avoid generic descriptions—be specific about why this club matters to you.
        </p>

        {/* Clubs List */}
        <div className="space-y-4">
          {(data.clubs || []).map((club, index) => (
            <div key={index} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <div className="flex items-start space-x-3">
                <div className="flex-1 space-y-3">
                  {/* Name */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Club Name #{index + 1}
                    </label>
                    <input
                      type="text"
                      value={club.name || ''}
                      onChange={(e) => updateClub(index, 'name', e.target.value)}
                      placeholder="e.g., Code for Good"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    />
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      What is this club/organization about?
                    </label>
                    <textarea
                      value={club.description || ''}
                      onChange={(e) => updateClub(index, 'description', e.target.value)}
                      placeholder="Brief description of what this club does and its focus"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-y min-h-[60px]"
                      style={{ height: 'auto', minHeight: '60px' }}
                      onInput={(e) => {
                        e.target.style.height = 'auto';
                        e.target.style.height = e.target.scrollHeight + 'px';
                      }}
                    />
                  </div>

                  {/* Community signal */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      What community or value does this signal?
                    </label>
                    <textarea
                      value={club.community_signal || ''}
                      onChange={(e) => updateClub(index, 'community_signal', e.target.value)}
                      placeholder="e.g., Students who code not for grades but to solve real problems in the community"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-y min-h-[60px]"
                      style={{ height: 'auto', minHeight: '60px' }}
                      onInput={(e) => {
                        e.target.style.height = 'auto';
                        e.target.style.height = e.target.scrollHeight + 'px';
                      }}
                    />
                  </div>
                </div>

                {/* Remove Button */}
                <button
                  onClick={() => removeClub(index)}
                  className="flex-shrink-0 p-2 text-gray-400 hover:text-red-600 transition"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}

          {/* Empty State */}
          {(!data.clubs || data.clubs.length === 0) && (
            <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
              <p className="text-sm text-gray-600 mb-3">No clubs added yet</p>
              <button
                onClick={addClub}
                className="inline-flex items-center space-x-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition"
              >
                <Plus className="w-4 h-4" />
                <span>Add Your First Club</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Field 3: Location Advantage (Optional) */}
      <div>
        <label className="block text-sm font-semibold text-gray-900 mb-2">
          Location Advantage (Optional)
          <span className="ml-2 text-xs font-normal text-gray-500">
            ({(data.location_advantage || '').length}/400 characters)
          </span>
        </label>
        <p className="text-sm text-gray-600 mb-3">
          If the school's location matters to you, explain what opportunities it enables.
          Be specific: not "great city" but "access to biotech startups for internships."
        </p>
        <textarea
          value={data.location_advantage || ''}
          onChange={(e) => onChange({ ...data, location_advantage: e.target.value })}
          placeholder='Example: "Being in Cambridge means I can walk to biotech labs at Kendall Square for internships"'
          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-y min-h-[100px]"
          style={{ height: 'auto', minHeight: '100px' }}
          onInput={(e) => {
            e.target.style.height = 'auto';
            e.target.style.height = e.target.scrollHeight + 'px';
          }}
        />
      </div>

      {/* Example/Tip Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-blue-900 mb-2">💡 Pro Tip</h4>
        <p className="text-sm text-blue-800">
          The best campus details show what you'd <em>do</em> in those spaces, not just that they exist.
          Instead of "The makerspace has 3D printers," try "I'd use the makerspace's laser cutters
          to prototype adaptive game controllers for students with disabilities."
        </p>
      </div>
    </div>
  );
}
